/* eslint-disable max-lines */
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { HealthSurveillanceSectionsConfig, MedicalTypeIds } from "../../models";

import { audiometryQuestions } from "./audiometryQuestions";
import { confidentialityQuestions } from "./confidentialityQuestions";
import { declarationOfHealthQuestions } from "./declarationOfHealthQuestions";
import { fieldForceQuestions } from "./fieldForceQuestions";
import { foodHandlerQuestions } from "./foodHandlerQuestions";
import { havsQuestions } from "./havsQuestions";
import { ionisingRadiationQuestions } from "./ionisingRadiationQuestions";
import { laaWorkerQuestions } from "./laaWorkerQuestions";
import { manualHandlingMskQuestions } from "./manualHandlingMskQuestions";
import { medicalContinuedQuestions } from "./medicalContinuedQuestions";
import { medicalQuestions } from "./medicalQuestions";
import { nightWorkerQuestions } from "./nightWorkerQuestions";
import { oeukQuestions } from "./oeukQuestions";
import { personalDetailsQuestions } from "./personalDetailsQuestions";
import { pitPupQuestions } from "./pitPupQuestions";
import { respiratoryQuestions } from "./respiratoryQuestions";
import { skinQuestions } from "./skinQuestions";
import { skinSectionTwoQuestions } from "./skinSectionTwoQuestions";
import { workingConfinedSpacesQuestions } from "./workingConfinedSpacesQuestions";

type QuestionnaireSectionKey = "ConfidentialityDeclaration"
    | "PersonalDetails"
    | "MedicalQuestions"
    | "MedicalQuestions2"
    | "WorkingConfinedSpaces"
    | "NightWorker"
    | "FoodHandler"
    | "Skin"
    | "Skin2"
    | "Audiometry"
    | "Respiratory"
    | "HAVS"
    | "IonisingRadiation"
    | "ManualHandlingMSK"
    | "PitPupQuestionnaire"
    | "LAAWorker"
    | "FieldForce"
    | "DeclarationOfHealth"
    | "Oeuk";

const enableNewNightWorkerQuestions = getFeatureToggles()[FeatureToggleNames.EnableNewNightWorkerQuestions];

export const sections: HealthSurveillanceSectionsConfig<QuestionnaireSectionKey> = {
    ConfidentialityDeclaration: {
        id: "ConfidentialityDeclaration",
        title: "Confidentiality Declaration",
        medicalTypes: [
            MedicalTypeIds.Skin,
            MedicalTypeIds.SkinFaceToFace,
            MedicalTypeIds.Audiometry,
            MedicalTypeIds.RespiratorySensitiser,
            MedicalTypeIds.RespiratorySensitiserFaceToFaceLevel1,
            MedicalTypeIds.RespiratorySensitiserQuestionnaireLevel1,
            MedicalTypeIds.HAVS,
            MedicalTypeIds.HAVSFaceToFace,
            MedicalTypeIds.IonisingRadiation,
            MedicalTypeIds.IonisingRadiationFaceToFace,
            MedicalTypeIds.BiologicalMonitoring,
            MedicalTypeIds.OtherSurveillance,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.GroupOneDriving,
            MedicalTypeIds.GroupTwoDriving,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.NightWorker,
            MedicalTypeIds.LoneWorker,
            MedicalTypeIds.FoodHandlerQuestionsOnly,
            MedicalTypeIds.DSE,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.ManualHandlingMSKQuestionnaire,
            MedicalTypeIds.ManualHandlingMSKFaceToFaceAppointment,
            MedicalTypeIds.AmzPitPupMedical,
            MedicalTypeIds.AmzPitPupHealthDeclaration,
            MedicalTypeIds.LAAWorker,
            MedicalTypeIds.FieldForce,
            MedicalTypeIds.SkinLevel1,
            MedicalTypeIds.SkinFaceToFaceLevel1,
            MedicalTypeIds.SkinAssessmentLevel2
        ],
        questionConfigs: confidentialityQuestions
    },
    PersonalDetails: {
        id: "PersonalDetails",
        title: "Personal Details",
        medicalTypes: [
            MedicalTypeIds.Skin,
            MedicalTypeIds.SkinFaceToFace,
            MedicalTypeIds.Audiometry,
            MedicalTypeIds.RespiratorySensitiser,
            MedicalTypeIds.RespiratorySensitiserFaceToFaceLevel1,
            MedicalTypeIds.RespiratorySensitiserQuestionnaireLevel1,
            MedicalTypeIds.HAVS,
            MedicalTypeIds.HAVSFaceToFace,
            MedicalTypeIds.IonisingRadiation,
            MedicalTypeIds.IonisingRadiationFaceToFace,
            MedicalTypeIds.BiologicalMonitoring,
            MedicalTypeIds.OtherSurveillance,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.GroupOneDriving,
            MedicalTypeIds.GroupTwoDriving,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.NightWorker,
            MedicalTypeIds.LoneWorker,
            MedicalTypeIds.FoodHandlerQuestionsOnly,
            MedicalTypeIds.DSE,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.ManualHandlingMSKQuestionnaire,
            MedicalTypeIds.ManualHandlingMSKFaceToFaceAppointment,
            MedicalTypeIds.AmzPitPupMedical,
            MedicalTypeIds.AmzPitPupHealthDeclaration,
            MedicalTypeIds.LAAWorker,
            MedicalTypeIds.FieldForce,
            MedicalTypeIds.SkinLevel1,
            MedicalTypeIds.SkinFaceToFaceLevel1,
            MedicalTypeIds.SkinAssessmentLevel2
        ],
        questionConfigs: personalDetailsQuestions
    },
    MedicalQuestions: {
        id: "MedicalQuestions",
        title: "Medical Questions",
        medicalTypes: [
            MedicalTypeIds.Skin,
            MedicalTypeIds.SkinFaceToFace,
            MedicalTypeIds.Audiometry,
            MedicalTypeIds.RespiratorySensitiser,
            MedicalTypeIds.RespiratorySensitiserFaceToFaceLevel1,
            MedicalTypeIds.RespiratorySensitiserQuestionnaireLevel1,
            MedicalTypeIds.HAVS,
            MedicalTypeIds.HAVSFaceToFace,
            MedicalTypeIds.IonisingRadiation,
            MedicalTypeIds.IonisingRadiationFaceToFace,
            MedicalTypeIds.BiologicalMonitoring,
            MedicalTypeIds.OtherSurveillance,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.GroupOneDriving,
            MedicalTypeIds.GroupTwoDriving,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            ...(enableNewNightWorkerQuestions ? [] : [MedicalTypeIds.NightWorker]),
            MedicalTypeIds.LoneWorker,
            MedicalTypeIds.FoodHandlerQuestionsOnly,
            MedicalTypeIds.DSE,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.ManualHandlingMSKQuestionnaire,
            MedicalTypeIds.ManualHandlingMSKFaceToFaceAppointment,
            MedicalTypeIds.AmzPitPupMedical,
            MedicalTypeIds.AmzPitPupHealthDeclaration,
            MedicalTypeIds.LAAWorker,
            MedicalTypeIds.FieldForce,
            MedicalTypeIds.SkinLevel1,
            MedicalTypeIds.SkinFaceToFaceLevel1,
            MedicalTypeIds.SkinAssessmentLevel2
        ],
        questionConfigs: medicalQuestions
    },
    MedicalQuestions2: {
        id: "MedicalQuestions2",
        title: "Medical Questions Cont'd",
        label: "Further Medical Questions",
        medicalTypes: [], // The whole section is only included with custom medicals, other medicals types include specific questions only
        questionConfigs: medicalContinuedQuestions
    },
    WorkingConfinedSpaces: {
        id: "WorkingConfindedSpaces",
        title: "Working in Confined Spaces",
        medicalTypes: [MedicalTypeIds.WorkingConfinedSpaces],
        questionConfigs: workingConfinedSpacesQuestions,
    },
    NightWorker: {
        id: "NightWorker",
        title: "Night Worker",
        medicalTypes: [MedicalTypeIds.NightWorker],
        questionConfigs: nightWorkerQuestions,
    },
    FoodHandler: {
        id: "FoodHandler",
        title: "Food Handler",
        medicalTypes: [MedicalTypeIds.FoodHandlerQuestionsOnly],
        questionConfigs: foodHandlerQuestions,
    },
    Skin: {
        id: "Skin",
        title: "Skin",
        medicalTypes: [
            MedicalTypeIds.Skin,
            MedicalTypeIds.SkinFaceToFace,
            MedicalTypeIds.LAAWorker,
            MedicalTypeIds.SkinLevel1,
            MedicalTypeIds.SkinFaceToFaceLevel1,
            MedicalTypeIds.SkinAssessmentLevel2
        ],
        questionConfigs: skinQuestions
    },
    Skin2: {
        id: "Skin2",
        title: "Skin Cont'd",
        label: "Detailed Skin",
        medicalTypes: [
            MedicalTypeIds.SkinFaceToFace,
            MedicalTypeIds.LAAWorker,
            MedicalTypeIds.SkinFaceToFaceLevel1,
            MedicalTypeIds.SkinAssessmentLevel2
        ],
        questionConfigs: skinSectionTwoQuestions
    },
    Audiometry: {
        id: "Audiometry",
        title: "Audiometry",
        medicalTypes: [
            MedicalTypeIds.Audiometry,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork
        ],
        questionConfigs: audiometryQuestions,
    },
    Respiratory: {
        id: "Respiratory",
        title: "Respiratory Sensitiser",
        label: "Respiratory",
        medicalTypes: [
            MedicalTypeIds.RespiratorySensitiser,
            MedicalTypeIds.RespiratorySensitiserFaceToFaceLevel1,
            MedicalTypeIds.RespiratorySensitiserQuestionnaireLevel1,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.LAAWorker
        ],
        questionConfigs: respiratoryQuestions,
    },
    HAVS: {
        id: "Havs",
        title: "HAVS",
        medicalTypes: [MedicalTypeIds.HAVS],
        questionConfigs: havsQuestions,
    },
    IonisingRadiation: {
        id: "IonisingRadiation",
        title: "Ionising Radiation",
        medicalTypes: [
            MedicalTypeIds.IonisingRadiation,
            MedicalTypeIds.IonisingRadiationFaceToFace
        ],
        questionConfigs: ionisingRadiationQuestions,
    },
    ManualHandlingMSK: {
        id: "ManualHandlingMSKQuestionnaire",
        title: "Manual Handling / MSK Questionnaire",
        label: "Manual Handling / MSK",
        medicalTypes: [
            MedicalTypeIds.ManualHandlingMSKQuestionnaire,
            MedicalTypeIds.ManualHandlingMSKFaceToFaceAppointment,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.GroupOneDriving,
            MedicalTypeIds.GroupTwoDriving,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
        ],
        questionConfigs: manualHandlingMskQuestions,
    },
    PitPupQuestionnaire: {
        id: "PitPupQuestionnaire",
        title: "PIT/PUP Medical Declaration",
        medicalTypes: [
            MedicalTypeIds.AmzPitPupMedical,
            MedicalTypeIds.AmzPitPupHealthDeclaration
        ],
        questionConfigs: pitPupQuestions,
    },
    LAAWorker: {
        id: "LAAWorker",
        title: "LAA Worker",
        medicalTypes: [MedicalTypeIds.LAAWorker],
        questionConfigs: laaWorkerQuestions,
    },
    FieldForce: {
        id: "FieldForce",
        title: "Field Force",
        medicalTypes: [MedicalTypeIds.FieldForce],
        questionConfigs: fieldForceQuestions,
    },
    DeclarationOfHealth: {
        id: "DelarationOfHealth",
        title: "Declaration of Health",
        medicalTypes: [
            MedicalTypeIds.Skin,
            MedicalTypeIds.SkinFaceToFace,
            MedicalTypeIds.Audiometry,
            MedicalTypeIds.RespiratorySensitiser,
            MedicalTypeIds.RespiratorySensitiserFaceToFaceLevel1,
            MedicalTypeIds.RespiratorySensitiserQuestionnaireLevel1,
            MedicalTypeIds.HAVS,
            MedicalTypeIds.HAVSFaceToFace,
            MedicalTypeIds.IonisingRadiation,
            MedicalTypeIds.IonisingRadiationFaceToFace,
            MedicalTypeIds.BiologicalMonitoring,
            MedicalTypeIds.OtherSurveillance,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.GroupOneDriving,
            MedicalTypeIds.GroupTwoDriving,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.NightWorker,
            MedicalTypeIds.LoneWorker,
            MedicalTypeIds.FoodHandlerQuestionsOnly,
            MedicalTypeIds.DSE,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.ManualHandlingMSKQuestionnaire,
            MedicalTypeIds.ManualHandlingMSKFaceToFaceAppointment,
            MedicalTypeIds.AmzPitPupMedical,
            MedicalTypeIds.AmzPitPupHealthDeclaration,
            MedicalTypeIds.LAAWorker,
            MedicalTypeIds.FieldForce,
            MedicalTypeIds.SkinLevel1,
            MedicalTypeIds.SkinFaceToFaceLevel1,
            MedicalTypeIds.SkinAssessmentLevel2
        ],
        questionConfigs: declarationOfHealthQuestions,
    },
    Oeuk: {
        id: "Oeuk",
        title: "OEUK",
        medicalTypes: [],
        questionConfigs: oeukQuestions
    }
};
